import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  Users,
  Search,
  Check,
  X,
  Sliders,
  RefreshCw,
  CheckCircle2,
  AppWindow,
  Filter,
} from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import {
  adminGetPermissionsMatrix,
  adminListAllApplications,
  adminUpdateUserPermission,
} from '../api/applications';
import { APP_ICON_MAP, APP_COLOR_MAP } from '../hooks/useApplications';

const PERMISSION_LEVELS = [
  { key: 'access', label: 'Access', desc: 'Can enter the application and see it in launcher' },
  { key: 'view', label: 'View', desc: 'Can view stored records and metadata' },
  { key: 'create', label: 'Create', desc: 'Can create new records' },
  { key: 'edit', label: 'Edit', desc: 'Can modify existing records' },
  { key: 'delete', label: 'Delete', desc: 'Can delete records from application' },
  { key: 'share', label: 'Share', desc: 'Can share records with team members' },
  { key: 'export', label: 'Export', desc: 'Can export plaintext records' },
  { key: 'reveal', label: 'Reveal', desc: 'Can unmask secrets, passwords, and CVV' },
  { key: 'copy', label: 'Copy', desc: 'Can copy credentials to clipboard' },
];

const ROLE_COLORS = {
  SUPER_ADMIN: '#ef4444',
  ADMIN: '#f59e0b',
  USER: '#3b82f6',
  TEAM_ADMIN: '#8b5cf6',
  TEAM_MEMBER: '#10b981',
};

export default function AdminPermissions() {
  const [apps, setApps] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState('success');
  const [updating, setUpdating] = useState(false);

  // Granular permission modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAppSlug, setSelectedAppSlug] = useState('passwords');
  const [modalPermissions, setModalPermissions] = useState({});

  const showToast = (msg, type = 'success') => {
    setToastMsg(msg);
    setToastType(type);
    setTimeout(() => setToastMsg(''), 3500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allApps, userMatrix] = await Promise.all([
        adminListAllApplications(),
        adminGetPermissionsMatrix(),
      ]);
      setApps(allApps || []);
      setMatrix(userMatrix || []);
    } catch (err) {
      showToast('Failed to load permissions matrix', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleQuickToggleAccess = async (userRow, appSlug) => {
    const currentPerms = userRow.permissions[appSlug] || { access: false };
    const nextAccess = !currentPerms.access;
    setUpdating(true);
    try {
      await adminUpdateUserPermission(userRow.user.id, appSlug, {
        ...currentPerms,
        access: nextAccess,
      });
      setMatrix((prev) =>
        prev.map((row) => {
          if (row.user.id === userRow.user.id) {
            return {
              ...row,
              permissions: {
                ...row.permissions,
                [appSlug]: { ...currentPerms, access: nextAccess },
              },
            };
          }
          return row;
        })
      );
      showToast(`${appSlug} access ${nextAccess ? 'granted to' : 'revoked from'} ${userRow.user.email}`);
    } catch (err) {
      showToast('Failed to update permission', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const openPermissionModal = (userRow, appSlug = 'passwords') => {
    setSelectedUser(userRow);
    setSelectedAppSlug(appSlug);
    const existing = userRow.permissions[appSlug] || {};
    setModalPermissions({
      access: existing.access ?? true,
      view: existing.view ?? true,
      create: existing.create ?? true,
      edit: existing.edit ?? true,
      delete: existing.delete ?? true,
      share: existing.share ?? false,
      export: existing.export ?? false,
      reveal: existing.reveal ?? true,
      copy: existing.copy ?? true,
    });
  };

  const handleSaveModalPermissions = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setUpdating(true);
    try {
      await adminUpdateUserPermission(selectedUser.user.id, selectedAppSlug, modalPermissions);
      setMatrix((prev) =>
        prev.map((row) => {
          if (row.user.id === selectedUser.user.id) {
            return {
              ...row,
              permissions: {
                ...row.permissions,
                [selectedAppSlug]: { ...modalPermissions },
              },
            };
          }
          return row;
        })
      );
      showToast(`Permissions saved for ${selectedUser.user.email}`);
      setSelectedUser(null);
    } catch (err) {
      showToast('Failed to save permissions', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const filteredMatrix = matrix.filter((row) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      row.user.email.toLowerCase().includes(q) ||
      (row.user.role && row.user.role.toLowerCase().includes(q));
    const matchesRole =
      roleFilter === 'all' || row.user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Columns shown in the matrix table
  const tableAppSlugs = [
    'passwords',
    'notes',
    'identities',
    'cards',
    'documents',
    'reminders',
    'tasks',
    'ssh-keys',
  ];

  const enabledApps = apps.filter((a) => a.enabled);
  const allRoles = [...new Set(matrix.map((r) => r.user.role).filter(Boolean))];

  return (
    <MainLayout>
      {toastMsg && (
        <div
          className={`admin-toast ${toastType === 'error' ? 'admin-toast-error' : 'admin-toast-success'} animate-fade-in`}
          style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999 }}
        >
          <CheckCircle2 size={16} />
          <span>{toastMsg}</span>
        </div>
      )}

      {/* Page Header - Full Bleed Edge-to-Edge */}
      <div className="admin-page-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="admin-header-title" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div
              className="admin-header-icon-badge notes-hero-icon-badge"
              style={{ background: 'rgba(255, 255, 255, 0.2)', color: '#ffffff' }}
            >
              <ShieldCheck size={24} />
            </div>
            <div>
              <h1 className="notes-hero-title">User Permissions Matrix</h1>
              <p className="notes-hero-subtitle">
                Manage granular application access levels for each user. Toggle access per app or
                configure all 9 permission levels.
              </p>
            </div>
          </div>
          <div className="admin-header-actions">
            <button
              className="btn-secondary notes-hero-btn-secondary"
              onClick={loadData}
              disabled={loading}
              title="Refresh permissions matrix"
            >
              <RefreshCw size={15} className={loading ? 'rotate-anim' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="admin-page-container">
        {/* Summary Cards */}
        <div className="admin-stats-grid" style={{ marginBottom: '24px' }}>
          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(59,130,246,0.12)', color: '#3b82f6' }}>
              <Users size={20} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Total Users</span>
              <strong className="admin-stat-value">{matrix.length}</strong>
            </div>
          </div>
          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(13,148,136,0.12)', color: '#0d9488' }}>
              <AppWindow size={20} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Active Apps</span>
              <strong className="admin-stat-value">{enabledApps.length}</strong>
            </div>
          </div>
          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(139,92,246,0.12)', color: '#8b5cf6' }}>
              <ShieldCheck size={20} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Permissions Set</span>
              <strong className="admin-stat-value">
                {matrix.reduce((acc, row) => acc + Object.keys(row.permissions || {}).length, 0)}
              </strong>
            </div>
          </div>
          <div className="admin-stat-card glass-panel">
            <div className="admin-stat-icon" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
              <Filter size={20} />
            </div>
            <div className="admin-stat-body">
              <span className="admin-stat-label">Showing</span>
              <strong className="admin-stat-value">{filteredMatrix.length}</strong>
            </div>
          </div>
        </div>

        {/* Permissions Matrix Table */}
        <div className="admin-table-container glass-panel">
          <div
            className="admin-table-toolbar"
            style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-color)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} color="var(--text-secondary)" />
              <strong>
                Permission Matrix — {filteredMatrix.length} of {matrix.length} users
              </strong>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Role Filter */}
              <select
                className="input-field"
                style={{ fontSize: '0.82rem', padding: '6px 12px', minWidth: '130px', height: '36px', boxSizing: 'border-box' }}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                aria-label="Filter by role"
              >
                <option value="all">All Roles</option>
                {allRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>

              {/* Search */}
              <div className="admin-search-box" style={{ width: '240px', height: '36px', boxSizing: 'border-box' }}>
                <Search size={15} color="var(--text-secondary)" />
                <input
                  type="text"
                  placeholder="Search by email or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="admin-search-input"
                  aria-label="Search users"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <RefreshCw size={20} className="rotate-anim" style={{ marginBottom: '12px' }} />
              <p>Loading permissions matrix...</p>
            </div>
          ) : filteredMatrix.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <ShieldCheck size={32} style={{ marginBottom: '12px', opacity: 0.4 }} />
              <p>No users found matching your search.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
              <table className="admin-table app-perms-table" style={{ width: '100%', minWidth: '960px' }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: '220px' }}>USER</th>
                    <th style={{ minWidth: '120px' }}>ROLE</th>
                    {tableAppSlugs.map((slug) => {
                      const appInfo = apps.find((a) => a.slug === slug);
                      const IconComp = APP_ICON_MAP[slug] || AppWindow;
                      const color = APP_COLOR_MAP[slug] || '#3b82f6';
                      return (
                        <th key={slug} style={{ textAlign: 'center', minWidth: '78px' }} title={appInfo?.name || slug}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <div style={{ color, opacity: 0.85 }}>
                              <IconComp size={14} />
                            </div>
                            <span style={{ fontSize: '0.67rem', letterSpacing: '0.04em' }}>
                              {(appInfo?.name || slug).toUpperCase().split(' ')[0]}
                            </span>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ textAlign: 'right', minWidth: '110px' }}>CONFIGURE</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatrix.map((row) => {
                    const u = row.user;
                    const isAdminUser = ['SUPER_ADMIN', 'ADMIN'].includes(u.role);
                    const roleColor = ROLE_COLORS[u.role] || '#6b7280';

                    return (
                      <tr key={u.id}>
                        <td>
                          <div className="admin-user-cell">
                            <div
                              className="user-avatar-sm"
                              style={{ background: roleColor }}
                            >
                              {u.email.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <strong style={{ fontSize: '0.84rem' }}>{u.email}</strong>
                              <small style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.73rem' }}>
                                {u.id?.substring(0, 8)}…
                              </small>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`role-chip role-${(u.role || 'user').toLowerCase()}`}
                            style={{ fontSize: '0.72rem' }}
                          >
                            {u.role || 'USER'}
                          </span>
                        </td>

                        {tableAppSlugs.map((slug) => {
                          const perm = row.permissions[slug] || { access: false };
                          const hasAccess = isAdminUser || perm.access;

                          return (
                            <td key={slug} style={{ textAlign: 'center' }}>
                              <button
                                type="button"
                                className={`app-perm-cell-btn ${hasAccess ? 'granted' : 'revoked'}`}
                                onClick={() => {
                                  if (isAdminUser) {
                                    showToast('Admins always have full access to all applications.');
                                    return;
                                  }
                                  handleQuickToggleAccess(row, slug);
                                }}
                                title={
                                  isAdminUser
                                    ? 'Admin — always has full access'
                                    : `${u.email}: Click to toggle ${slug} access`
                                }
                                disabled={isAdminUser || updating}
                                aria-label={`${hasAccess ? 'Revoke' : 'Grant'} ${slug} access for ${u.email}`}
                              >
                                {hasAccess ? <Check size={13} /> : <X size={13} />}
                              </button>
                            </td>
                          );
                        })}

                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => openPermissionModal(row)}
                            title="Open granular 9-level permission configurator"
                            disabled={isAdminUser}
                          >
                            <Sliders size={13} />
                            <span>Configure</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Granular Permission Editor Modal */}
        {selectedUser && (
          <div className="modal-backdrop" onClick={() => setSelectedUser(null)}>
            <div
              className="modal-card glass-panel"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '640px' }}
            >
              <div className="modal-header">
                <div
                  className="modal-header-icon"
                  style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}
                >
                  <Sliders size={20} />
                </div>
                <div>
                  <h3>Granular Permission Editor</h3>
                  <p>
                    Configuring <strong>{selectedUser.user.email}</strong>
                  </p>
                </div>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setSelectedUser(null)}
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSaveModalPermissions}>
                <div className="modal-body-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Application selector */}
                  <div className="form-group">
                    <label>Target Application</label>
                    <select
                      className="input-field"
                      value={selectedAppSlug}
                      onChange={(e) => {
                        const newSlug = e.target.value;
                        setSelectedAppSlug(newSlug);
                        const existing = selectedUser.permissions[newSlug] || {};
                        setModalPermissions({
                          access: existing.access ?? true,
                          view: existing.view ?? true,
                          create: existing.create ?? true,
                          edit: existing.edit ?? true,
                          delete: existing.delete ?? true,
                          share: existing.share ?? false,
                          export: existing.export ?? false,
                          reveal: existing.reveal ?? true,
                          copy: existing.copy ?? true,
                        });
                      }}
                    >
                      {apps.map((a) => (
                        <option key={a.slug} value={a.slug}>
                          {a.name} ({a.category})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 9 permission toggles */}
                  <div className="perms-matrix-list">
                    {PERMISSION_LEVELS.map((lvl) => {
                      const isChecked = Boolean(modalPermissions[lvl.key]);
                      return (
                        <div key={lvl.key} className="perm-toggle-row">
                          <div className="perm-toggle-copy">
                            <strong>{lvl.label}</strong>
                            <small>{lvl.desc}</small>
                          </div>
                          <button
                            type="button"
                            className={`toggle-switch ${isChecked ? 'on' : 'off'}`}
                            onClick={() =>
                              setModalPermissions((prev) => ({ ...prev, [lvl.key]: !isChecked }))
                            }
                          >
                            <span className="toggle-thumb" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="modal-actions" style={{ marginTop: '20px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setSelectedUser(null)}
                    disabled={updating}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={updating}>
                    {updating ? 'Saving...' : 'Apply Permissions'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
